/**
 * DiffuserComparisonPage.tsx — Candidate Diffuser Comparison Matrix
 *
 * Puts up to six candidate diffusers side-by-side on a single page so the
 * client can see the down-selection landscape, not just the chosen unit.
 *
 * Bound to the active session for the working submergence & operating
 * conditions; the diffuser definitions themselves are a curated long-list
 * (membrane disc EPDM/PTFE, ceramic dome, EPDM tubular, HDPE porous tube,
 * polyurethane disc).
 *
 * Each candidate gets a scored row across:
 *   - SOTE per metre at site submergence
 *   - Per-module coverage area
 *   - Modules required for full Barcombe coverage
 *   - AWWA M37 compliance flag
 *   - CAPEX band (£ per module — bands, not absolute, no margin disclosure)
 *   - Fouling resistance score
 *   - Embodied carbon band
 *   - Pilot-trial suitability score
 *
 * Methodology held internally — only the scored outputs are surfaced.
 */

import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  Grid3X3, ArrowLeft, RefreshCcw, CheckCircle2, AlertTriangle,
  Printer, ShieldCheck, ShieldAlert,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import {
  specFromLegacy, type LegacyModSpec,
  bubbleDiameterMm, sotePerMetre, moduleBedCoverageAreaM2,
} from '@/lib/diffuserPhysics';
import AwwaM37Toggle, { useAwwaOverride, awwaClassify } from '@/components/AwwaM37Toggle';
import { generateDiffuserComparisonPDF } from '@/lib/reservoirPdf';
import { getProfile, DEFAULT_PROFILE_ID } from '@/lib/seasonalProfiles';

const RESERVOIR_AREA_M2 = 34_000;
const RESERVOIR_AREA_HA = RESERVOIR_AREA_M2 / 10_000;

interface ModSpec extends LegacyModSpec {
  waterDepthM: number;
  mountingHeightM: number;
  waterTempC: number;
}

const DEFAULTS: ModSpec = {
  diffuserCount: 8,
  diffuserType: 'membrane_disc_epdm',
  diffuserDiamMm: 300,
  poreSizeUm: 80,
  airflowPerDiffNm3h: 1.2,
  waterDepthM: 3.0,
  mountingHeightM: 0.40,
  waterTempC: 12,
};

interface Candidate {
  id: string;
  label: string;
  diffuserType: string;
  diffuserDiamMm?: number;
  poreSizeUm: number;
  airflow: number;
  // Qualitative bands (1–5)
  foulingResistance: number;   // 5 = very resistant
  embodiedCarbonBand: number;  // 5 = lowest
  capexBand: 'low' | 'mid' | 'high';
  pilotSuitability: number;    // 5 = excellent fit for pilot trial
  description: string;
  // Tubular geometry (if applicable)
  tubeLengthM?: number;
  tubeCount?: number;
  tubeDiamMm?: number;
  tubePattern?: 'perimeter' | 'parallel' | 'cross' | 'centre_radial';
  porePerCm?: number;
}

const CANDIDATES: Candidate[] = [
  {
    id: 'epdm_disc_300',
    label: 'EPDM Membrane Disc Ø300',
    diffuserType: 'membrane_disc_epdm',
    diffuserDiamMm: 300, poreSizeUm: 80, airflow: 1.2,
    foulingResistance: 3, embodiedCarbonBand: 4, capexBand: 'low', pilotSuitability: 5,
    description: 'Industry workhorse, predictable SOTE, surfactant-sensitive. Lowest CAPEX.',
  },
  {
    id: 'ptfe_disc_300',
    label: 'PTFE Membrane Disc Ø300',
    diffuserType: 'membrane_disc_ptfe',
    diffuserDiamMm: 300, poreSizeUm: 60, airflow: 1.0,
    foulingResistance: 5, embodiedCarbonBand: 2, capexBand: 'high', pilotSuitability: 4,
    description: 'PTFE laminate — highest fouling resistance, smallest bubbles, highest CAPEX.',
  },
  {
    id: 'ceramic_dome',
    label: 'Ceramic Sintered Dome Ø230',
    diffuserType: 'ceramic_dome',
    diffuserDiamMm: 230, poreSizeUm: 100, airflow: 1.5,
    foulingResistance: 4, embodiedCarbonBand: 3, capexBand: 'high', pilotSuitability: 3,
    description: 'Long-life ceramic, robust, larger bubbles. Higher CAPEX, low maintenance.',
  },
  {
    id: 'epdm_tube_2m',
    label: 'EPDM Tubular 2.0 m',
    diffuserType: 'epdm_porous_tube',
    poreSizeUm: 80, airflow: 1.4,
    tubeLengthM: 2.0, tubeCount: 1, tubeDiamMm: 75, tubePattern: 'parallel', porePerCm: 10,
    foulingResistance: 3, embodiedCarbonBand: 4, capexBand: 'mid', pilotSuitability: 4,
    description: 'Line source — elongated plume footprint, useful for long narrow zones.',
  },
  {
    id: 'hdpe_tube_2m',
    label: 'HDPE Porous Tube 2.0 m',
    diffuserType: 'hdpe_porous_tube',
    poreSizeUm: 120, airflow: 1.6,
    tubeLengthM: 2.0, tubeCount: 1, tubeDiamMm: 75, tubePattern: 'parallel', porePerCm: 8,
    foulingResistance: 5, embodiedCarbonBand: 5, capexBand: 'mid', pilotSuitability: 3,
    description: 'High fouling resistance, lower SOTE — strong for high-iron raw water.',
  },
  {
    id: 'pu_disc_270',
    label: 'Polyurethane Disc Ø270',
    diffuserType: 'membrane_disc_epdm',
    diffuserDiamMm: 270, poreSizeUm: 70, airflow: 1.1,
    foulingResistance: 4, embodiedCarbonBand: 3, capexBand: 'mid', pilotSuitability: 4,
    description: 'Polyurethane laminate — mid-range fouling, good for variable load.',
  },
];

interface Scored {
  c: Candidate;
  d_b_mm: number;
  sotePerM: number;
  coverageM2: number;
  modulesForFullCoverage: number;
  awwaOk: boolean;
  awwaMin: number;
  awwaMax: number;
  // Weighted overall score (1–5)
  overallScore: number;
}

function scoreCandidate(c: Candidate, baseSpec: ModSpec, awwaOverride: boolean): Scored {
  const H_eff = Math.max(0.3, baseSpec.waterDepthM - baseSpec.mountingHeightM);
  // Map candidate into a ModSpec
  const cSpec: ModSpec = {
    ...baseSpec,
    diffuserType: c.diffuserType,
    diffuserDiamMm: c.diffuserDiamMm ?? baseSpec.diffuserDiamMm,
    poreSizeUm: c.poreSizeUm,
    airflowPerDiffNm3h: c.airflow,
    tubeLengthM: c.tubeLengthM,
    tubeCount: c.tubeCount,
    tubeDiamMm: c.tubeDiamMm,
    tubePattern: c.tubePattern,
    porePerCm: c.porePerCm,
  };
  const dSpec = specFromLegacy(cSpec);
  const d_b_mm = bubbleDiameterMm(dSpec, baseSpec.waterTempC);
  const sotePerM_val = sotePerMetre(d_b_mm);
  const coverageM2 = moduleBedCoverageAreaM2(dSpec, H_eff) * 0.85;  // 15% overlap
  const modulesForFullCoverage = Math.ceil(RESERVOIR_AREA_M2 / Math.max(1, coverageM2));
  const cls = awwaClassify(modulesForFullCoverage, RESERVOIR_AREA_HA, awwaOverride);
  const awwaMin = cls.min;
  const awwaMax = cls.max;
  const awwaOk = cls.ok;

  // Weighted overall score (1-5)
  const soteScore   = Math.max(1, Math.min(5, sotePerM_val / 1.5));
  const moduleScore = Math.max(1, Math.min(5, 5 - (modulesForFullCoverage - awwaMin) / Math.max(1, awwaMax - awwaMin) * 4));
  const capexScore  = c.capexBand === 'low' ? 5 : c.capexBand === 'mid' ? 3.5 : 2;
  const overallScore = (
    soteScore * 0.25 +
    moduleScore * 0.20 +
    c.foulingResistance * 0.15 +
    c.embodiedCarbonBand * 0.10 +
    capexScore * 0.15 +
    c.pilotSuitability * 0.15
  );

  return {
    c, d_b_mm, sotePerM: sotePerM_val, coverageM2,
    modulesForFullCoverage, awwaOk, awwaMin, awwaMax, overallScore,
  };
}

function fmt(n: number, d = 2) {
  if (!isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

function StarBar({ score, max = 5 }: { score: number; max?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: max }).map((_, i) => (
        <div key={i} className={`w-2 h-3 rounded-sm ${i < Math.round(score) ? 'bg-rb-brand' : 'bg-slate-700'}`} />
      ))}
      <span className="ml-1.5 text-xs text-slate-300 tabular-nums">{score.toFixed(1)}</span>
    </div>
  );
}

export default function DiffuserComparisonPage() {
  const navigate = useNavigate();
  const [spec, setSpec] = useState<ModSpec>(() => loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS));
  const [enabled, setEnabled] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(CANDIDATES.map(c => [c.id, true])));
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const unsub = onPersistChange((key) => {
      if (key === KEYS.DIFFUSER_MODULE) {
        setSpec(loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS));
        setNonce(n => n + 1);
      }
    });
    return () => unsub();
  }, []);

  const { enabled: awwaOverride } = useAwwaOverride();

  const scored = useMemo(() => {
    return CANDIDATES
      .filter(c => enabled[c.id])
      .map(c => scoreCandidate(c, spec, awwaOverride))
      .sort((a, b) => b.overallScore - a.overallScore);
  }, [spec, enabled, awwaOverride, nonce]);

  const winner = scored[0];

  const exportPDF = () => {
    const profile = getProfile(DEFAULT_PROFILE_ID);
    generateDiffuserComparisonPDF({
      reservoirAreaM2: RESERVOIR_AREA_M2,
      waterDepthM: spec.waterDepthM,
      mountingHeightM: spec.mountingHeightM,
      waterTempC: spec.waterTempC,
      candidates: scored.map(s => ({
        label: s.c.label,
        d_b_mm: s.d_b_mm,
        sotePerM: s.sotePerM,
        coverageM2: s.coverageM2,
        modulesForFullCoverage: s.modulesForFullCoverage,
        awwaOk: s.awwaOk,
        foulingResistance: s.c.foulingResistance,
        embodiedCarbonBand: s.c.embodiedCarbonBand,
        capexBand: s.c.capexBand,
        pilotSuitability: s.c.pilotSuitability,
        overallScore: s.overallScore,
        description: s.c.description,
      })),
    }, profile);
  };

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Grid3X3}
        title="Diffuser Candidate Comparison"
        subtitle="Side-by-side scoring of the long-list against the active session conditions"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <AwwaM37Toggle compact />
            <button onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={exportPDF}
              className="inline-flex items-center gap-2 rounded-lg bg-rb-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-rb-brand/90">
              <Printer className="w-4 h-4" /> Export PDF
            </button>
          </div>
        }
      />

      {/* Active session banner */}
      <div className="rounded-xl border border-rb-brand/30 bg-rb-brand/5 p-4">
        <div className="text-sm font-semibold text-slate-100">Active session conditions</div>
        <div className="text-xs text-slate-400 mt-0.5">
          Water depth {fmt(spec.waterDepthM, 1)} m · Mounting {fmt(spec.mountingHeightM, 2)} m · Water temp {fmt(spec.waterTempC, 1)} °C · Reservoir {RESERVOIR_AREA_M2.toLocaleString()} m² ({RESERVOIR_AREA_HA.toFixed(2)} ha)
        </div>
      </div>

      {/* Winner callout */}
      {winner && (
        <div className="rounded-xl border-2 border-emerald-700 bg-emerald-900/20 p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Top-ranked candidate (pre-pilot)</div>
              <div className="text-lg font-bold text-slate-100 mt-0.5">{winner.c.label}</div>
              <div className="text-xs text-slate-400 mt-1">{winner.c.description}</div>
              <div className="text-xs text-slate-300 mt-2">
                Overall score <span className="font-bold text-emerald-300">{winner.overallScore.toFixed(2)}/5</span> ·
                SOTE/m {fmt(winner.sotePerM, 2)} % · Modules for full coverage {winner.modulesForFullCoverage} · AWWA {winner.awwaOk ? 'PASS' : 'CHECK'}
              </div>
            </div>
            <button
              onClick={() => { setSpec(loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS)); setNonce(n => n + 1); }}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-700">
              <RefreshCcw className="w-3.5 h-3.5" /> Reload
            </button>
          </div>
        </div>
      )}

      {/* Candidate enable toggles */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Include in comparison</div>
        <div className="flex flex-wrap gap-2">
          {CANDIDATES.map(c => (
            <label key={c.id}
              className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 cursor-pointer transition ${
                enabled[c.id] ? 'border-rb-brand bg-rb-brand/10 text-slate-100' : 'border-slate-700 bg-slate-800 text-slate-400'
              }`}>
              <input type="checkbox" checked={enabled[c.id]}
                onChange={e => setEnabled({ ...enabled, [c.id]: e.target.checked })}
                className="accent-rb-brand" />
              <span className="text-xs">{c.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Comparison table */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-800 border-b border-slate-700">
              <tr>
                <th className="text-left px-3 py-2 text-slate-300 font-semibold">Rank</th>
                <th className="text-left px-3 py-2 text-slate-300 font-semibold">Candidate</th>
                <th className="text-right px-3 py-2 text-slate-300 font-semibold">Bubble Ø</th>
                <th className="text-right px-3 py-2 text-slate-300 font-semibold">SOTE/m</th>
                <th className="text-right px-3 py-2 text-slate-300 font-semibold">Coverage</th>
                <th className="text-right px-3 py-2 text-slate-300 font-semibold">Modules</th>
                <th className="text-center px-3 py-2 text-slate-300 font-semibold">AWWA</th>
                <th className="text-center px-3 py-2 text-slate-300 font-semibold">Fouling</th>
                <th className="text-center px-3 py-2 text-slate-300 font-semibold">Carbon</th>
                <th className="text-center px-3 py-2 text-slate-300 font-semibold">CAPEX</th>
                <th className="text-center px-3 py-2 text-slate-300 font-semibold">Pilot fit</th>
                <th className="text-center px-3 py-2 text-slate-300 font-semibold">Overall</th>
              </tr>
            </thead>
            <tbody>
              {scored.map((s, i) => (
                <tr key={s.c.id} className={`border-b border-slate-800 ${i === 0 ? 'bg-emerald-900/10' : ''}`}>
                  <td className="px-3 py-2 text-slate-100 font-bold">{i + 1}</td>
                  <td className="px-3 py-2">
                    <div className="text-slate-100 font-medium">{s.c.label}</div>
                    <div className="text-slate-500 text-[10px] mt-0.5">{s.c.description}</div>
                  </td>
                  <td className="px-3 py-2 text-right text-slate-100 tabular-nums">{fmt(s.d_b_mm, 2)} mm</td>
                  <td className="px-3 py-2 text-right text-slate-100 tabular-nums">{fmt(s.sotePerM, 2)} %</td>
                  <td className="px-3 py-2 text-right text-slate-100 tabular-nums">{fmt(s.coverageM2, 0)} m²</td>
                  <td className="px-3 py-2 text-right text-slate-100 tabular-nums">{s.modulesForFullCoverage}</td>
                  <td className="px-3 py-2 text-center">
                    {awwaOverride
                      ? <span className="text-[10px] text-violet-300 font-bold">BYPASS</span>
                      : s.awwaOk
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-400 inline" />
                        : <AlertTriangle className="w-4 h-4 text-amber-400 inline" />}
                  </td>
                  <td className="px-3 py-2"><StarBar score={s.c.foulingResistance} /></td>
                  <td className="px-3 py-2"><StarBar score={s.c.embodiedCarbonBand} /></td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      s.c.capexBand === 'low' ? 'bg-emerald-900/40 text-emerald-300' :
                      s.c.capexBand === 'mid' ? 'bg-amber-900/40 text-amber-300' :
                      'bg-red-900/40 text-red-300'
                    }`}>{s.c.capexBand.toUpperCase()}</span>
                  </td>
                  <td className="px-3 py-2"><StarBar score={s.c.pilotSuitability} /></td>
                  <td className="px-3 py-2 text-center">
                    <span className="text-lg font-bold text-rb-brand tabular-nums">{s.overallScore.toFixed(2)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Scoring methodology disclosure */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-rb-brand" /> Scoring methodology
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            The overall score is a weighted combination of (a) computed performance against the active session
            (bubble size, SOTE per metre, coverage area, module count, AWWA M37 compliance) and (b) qualitative
            ratings for fouling resistance, embodied carbon, CAPEX band and pilot-trial suitability.
          </p>
          <p>
            The ranking is a <strong>pre-pilot</strong> indication only. A final recommendation requires the
            full pre-selection programme: site-measured sediment oxygen demand, surfactant index, bathymetric
            survey and an eight-week side-by-side pilot trial against the top two or three candidates.
            Detailed CAPEX figures, supplier identities and pilot empirical results are not surfaced on this page.
          </p>
        </div>
      </div>
    </div>
  );
}
